/**
 * Configuración del enlace de compartir del planner embebido (HRS-2890).
 *
 * `share_base` (query param del src del iframe) es la fuente de verdad del
 * enlace: la web anfitriona lo manda ya resuelto con su idioma y slug, así un
 * cambio de slug o un idioma nuevo en su web no requiere publicar versión del
 * front. Llega tanto en el widget del chatbot (/chatbot-embed) como en el site
 * embebido en modo planner (?planner=), por eso se resuelve en app.component
 * para toda la app.
 */

/**
 * Allowlist GLOBAL de hosts válidos para `share_base` (match exacto de
 * `URL.host`). Es global —no por chatbot— porque se valida en el bootstrap de
 * app.component, donde el chatbotId aún no está resuelto (en el embed real
 * llega por postMessage) y el modo planner ni siquiera tiene chatbot. Unión de
 * los hosts de los clientes que usan share_base; si algún día colisionan
 * clientes, reevaluar un scoping por tenant.
 *
 * Hosts de Visit Madrid confirmados por el cliente (comentario 2026-08-03 en
 * HRS-2890): producción (www + apex) y sus entornos de validación/desarrollo.
 * Su share_base se construye re-basado al dominio del entorno donde corre su
 * web, así que los cuatro tienen que estar desde el principio.
 */
export const SHARE_BASE_ALLOWED_HOSTS: readonly string[] = [
  'www.visitmadrid.es',
  'visitmadrid.es',
  'visit.val.comunidad.madrid',
  'visit.des.comunidad.madrid',
];

/** Mapa fallback por idioma del embed: red de seguridad para que el botón de
 *  compartir nunca se rompa si `share_base` no llega o no valida. */
export interface EmbedShareConfig {
  /** URL del planner del cliente por subtag base de idioma (claves ya normalizadas: `pt-BR` → `pt`, `zh-hans` → `zh`). */
  fallbackShareUrlByLang: Readonly<Record<string, string>>;
  /** Red de seguridad final: si el idioma actual no está en el mapa. */
  defaultFallbackShareUrl: string;
}

export const EMBED_SHARE_CONFIG: Record<string, EmbedShareConfig> = {
  // Visit Madrid (chatbot "Sol", cliente 130) — HRS-2890 / CMAD-142.
  '133': {
    fallbackShareUrlByLang: {
      es: 'https://www.visitmadrid.es/crea-tu-itinerario-madrid',
      de: 'https://www.visitmadrid.es/de/erstellen-sie-ihre-reiseroute',
      en: 'https://www.visitmadrid.es/en/create-your-itinerary-in-madrid',
      fr: 'https://www.visitmadrid.es/fr/creez-votre-itineraire-madrid',
      it: 'https://www.visitmadrid.es/it/crea-il-tuo-itinerario-madrid',
      ja: 'https://www.visitmadrid.es/ja/matoritonoluchengwozuocheng',
      ko: 'https://www.visitmadrid.es/ko/yeohaeng-iljeongeul-mandeuleoboseyo',
      pt: 'https://www.visitmadrid.es/pt-pt/crie-seu-roteiro-em-madri',
      zh: 'https://www.visitmadrid.es/zh-hans/chuangjiannindemadelixingcheng',
    },
    defaultFallbackShareUrl: 'https://www.visitmadrid.es/crea-tu-itinerario-madrid',
  },
};

export function resolveEmbedShareConfig(chatbotId: string | null | undefined): EmbedShareConfig | null {
  if (!chatbotId) return null;
  return EMBED_SHARE_CONFIG[String(chatbotId)] ?? null;
}
